import { Heading, SvgIcon } from '@cinch-labs/design-system'
import cx from 'classnames'
import type { MouseEvent } from 'react'
import { Children, useContext, useId, useRef, useState } from 'react'
import { flushSync } from 'react-dom'

import {
  AccordionBusContext,
  useClientLayoutEffect,
} from './accordion-group.context'
import { styles } from './accordion-item.styles'
import type { AccordionItemProps } from './accordion-item.types'

/** The nearest ancestor that scrolls, or null when the page itself does. */
const getScrollParent = (element: HTMLElement): HTMLElement | null => {
  // While HTML's overflow is visible, an overflow set on the body scrolls the
  // page, and the body is not a scroll container of its own.
  const isBodyThePage =
    getComputedStyle(document.documentElement).overflowY === 'visible'

  for (let node = element.parentElement; node; node = node.parentElement) {
    if (node === document.body && isBodyThePage) {
      return null
    }

    const { overflowY } = getComputedStyle(node)
    const canScroll = overflowY === 'auto' || overflowY === 'scroll'

    if (canScroll && node.scrollHeight > node.clientHeight) {
      return node
    }
  }

  return null
}

/**
 * One expandable row of an Accordion. The header shows the optional icon and
 * the title, and the children are the content shown when the item is expanded.
 *
 * Each item owns its expanded state. It starts collapsed unless `expanded` is
 * set, and several items can start expanded. Opening an item tells the others
 * in the same accordion, and each enabled one closes itself; the open item can
 * be closed so that none are. A disabled item does not listen, so it keeps the
 * state it has, open or collapsed.
 *
 * An item reads its colours and sizes from the surrounding Accordion, so it must
 * be rendered inside one. Rendered alone, it still works on its own; that
 * exists for its tests, not as a supported way to use it.
 *
 * [View in Figma](https://www.figma.com/design/uk4HuDPdSGgZMSi3iybv5V/Servicing-Landing-Pages?node-id=3109-51886)
 */
export function AccordionItem(props: AccordionItemProps) {
  const {
    expanded = false,
    isDisabled = false,
    onOpenChange,
    title,
    iconName,
    children,
    className,
    ...htmlProps
  } = props

  const bus = useContext(AccordionBusContext)
  const [isExpanded, setIsExpanded] = useState(expanded)
  const id = useId()
  const focusAtPressRef = useRef<Element | null>(null)

  // Another item opening closes this one. A disabled item stops listening, so
  // it keeps its state whatever the others do.
  useClientLayoutEffect(() => {
    if (!bus || isDisabled) {
      return
    }

    return bus.subscribe((openedId) => {
      if (openedId !== id) {
        setIsExpanded(false)
      }
    })
  }, [bus, id, isDisabled])

  if (Children.toArray(children).length === 0) {
    return null
  }

  const triggerId = `${id}-trigger`
  const panelId = `${id}-panel`

  // WebKit moves focus to the page body on mousedown, before the click, so the
  // element that held focus is noted here for the click to use.
  const rememberFocus = () => {
    focusAtPressRef.current = document.activeElement
  }

  // The trigger is aria-disabled rather than disabled, so a header disabled
  // while it has focus keeps it. Unlike a natively disabled button, it still
  // receives clicks and key presses, so the toggle has to ignore them itself.
  const toggle = (event: MouseEvent<HTMLButtonElement>) => {
    const focusAtPress = focusAtPressRef.current
    focusAtPressRef.current = null

    if (isDisabled) {
      return
    }

    const nextIsExpanded = !isExpanded
    const trigger = event.currentTarget
    const topBefore = trigger.getBoundingClientRect().top
    const focused =
      document.activeElement === document.body
        ? focusAtPress
        : document.activeElement

    // Flushed, so the other items have closed before the header is measured
    // again below.
    flushSync(() => {
      setIsExpanded(nextIsExpanded)
      if (nextIsExpanded) {
        bus?.announceOpen(id)
      }
    })

    // A panel closing above this header moves it up by the panel's height,
    // often off-screen on a phone. Scrolling back by the same amount before
    // paint keeps it under the finger. 'instant' stops a page-wide
    // `scroll-behavior: smooth` from animating the correction.
    const shift = trigger.getBoundingClientRect().top - topBefore
    if (shift !== 0) {
      const scrollParent = getScrollParent(trigger)
      const options: ScrollToOptions = { top: shift, behavior: 'instant' }

      if (scrollParent) {
        scrollParent.scrollBy(options)
      } else {
        window.scrollBy(options)
      }
    }

    // Safari does not focus a button on click, so focus can sit inside a
    // panel that just closed. It moves to this header instead of falling to
    // the page body.
    if (focused !== trigger && focused?.closest('[role="region"][hidden]')) {
      trigger.focus({ preventScroll: true })
    }

    onOpenChange?.(nextIsExpanded)
  }

  return (
    <div
      {...htmlProps}
      className={cx(styles.base, className)}
      data-state={isExpanded ? 'expanded' : 'collapsed'}
    >
      <Heading element="h3" className={styles.heading}>
        <button
          type="button"
          id={triggerId}
          className={styles.trigger}
          aria-expanded={isExpanded}
          aria-controls={panelId}
          aria-disabled={isDisabled || undefined}
          tabIndex={isDisabled ? -1 : undefined}
          onMouseDown={rememberFocus}
          onClick={toggle}
        >
          {/* The item's id ends the SvgIcons' gradient ids in place of a random
              suffix, so they are unique across items and the same on every
              mount, and the snapshots need no Math.random mock. The leading
              icon adds `-icon` so it cannot clash with the chevron. */}
          {iconName && (
            <span className={styles.icon} aria-hidden="true">
              <SvgIcon
                name={iconName}
                id={`${id}-icon`}
                aria-hidden="true"
                focusable="false"
              />
            </span>
          )}
          <span className={styles.title}>{title}</span>
          <span className={styles.action} aria-hidden="true">
            <SvgIcon
              name="chevron-down"
              id={id}
              aria-hidden="true"
              focusable="false"
            />
          </span>
        </button>
      </Heading>

      <div
        id={panelId}
        role="region"
        aria-labelledby={triggerId}
        className={styles.panel}
        hidden={!isExpanded}
      >
        {children}
      </div>
    </div>
  )
}

export default AccordionItem
